import React, { useMemo, useState, useEffect } from 'react';
import { Autocomplete, Box, Button, Paper, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TextField, Typography } from '@mui/material';

const ORANGE = '#F07828';
const BLUE = '#1565C0';
const BORDER = 'rgba(25,118,210,0.20)';

export function PageShell({ title, subtitle, children }) {
  return <Box sx={{ p: 2, bgcolor: '#F2F2F2', minHeight: '100vh' }}>
    <Paper sx={{ overflow: 'hidden', borderRadius: 2, border: `1px solid ${BORDER}` }}>
      <Box sx={{ px: 2, py: 1.5, color: '#fff', background: 'linear-gradient(90deg,#F07828,#ed7f35)' }}>
        <Typography variant="h6" fontWeight={900}>{title}</Typography>
        <Typography variant="body2">{subtitle}</Typography>
      </Box>
      <Box sx={{ p: 2 }}><Paper variant="outlined" sx={{ borderColor: BORDER, borderRadius: 2, p: 2 }}>{children}</Paper></Box>
    </Paper>
  </Box>;
}

export function Section({ title, children }) {
  return <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, p: 2, mb: 2 }}>
    <Typography sx={{ fontWeight: 900, color: BLUE, bgcolor: '#FFF0E3', borderRadius: 1.5, px: 1.5, py: 1, mb: 2 }}>{title}</Typography>
    {children}
  </Paper>;
}

// Filters appear in their own card above the table, never in table headers.
// Only data columns are filterable; action/render-only columns are excluded.
export function DataTable({ columns = [], rows = [], empty = 'No records found', tableId }) {
  const [filters, setFilters] = useState({});
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);
  const data = Array.isArray(rows) ? rows : [];
  const filterColumns = columns.filter(c => c.key && !['actions', 'action', 'models'].includes(c.key) && !c.render && !['status'].includes(c.key));
  const optionsByKey = useMemo(() => Object.fromEntries(filterColumns.map(c => [c.key, [...new Set(data.map(r => r?.[c.key]).filter(v => v !== null && v !== undefined && String(v).trim() !== '').map(String))].sort((a,b) => a.localeCompare(b, undefined, { numeric: true }))])), [data, columns]);
  const visible = useMemo(() => data.filter(row => Object.entries(filters).every(([key, selected]) => !selected?.length || selected.includes(String(row?.[key] ?? '')))), [data, filters]);
  useEffect(() => setPage(0), [filters, perPage, tableId]);
  const safePage = Math.min(page, Math.max(0, Math.ceil(visible.length / perPage) - 1));
  return <Stack spacing={2}>
    {filterColumns.length > 0 && <Paper elevation={0} sx={{ p: 2, border: `1px solid ${BORDER}`, borderRadius: 2 }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1.5 }}>
        <Typography fontWeight={900} color={BLUE}>Filters</Typography>
        <Button size="small" sx={{ color: ORANGE, textTransform: 'none', fontWeight: 800 }} onClick={() => setFilters({})}>Clear Filters</Button>
      </Stack>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(190px,1fr))', gap: 1.5 }}>
        {filterColumns.map(c => <Autocomplete key={c.key} multiple size="small" disableCloseOnSelect options={optionsByKey[c.key] || []}
          value={filters[c.key] || []} onChange={(_, values) => setFilters(prev => ({ ...prev, [c.key]: values }))}
          renderInput={params => <TextField {...params} label={c.label} placeholder={(filters[c.key] || []).length ? '' : 'Select...'} size="small" sx={{ '& .MuiOutlinedInput-root fieldset': { borderColor: BORDER }, '& .Mui-focused fieldset': { borderColor: ORANGE } }} />} />)}
      </Box>
    </Paper>}
    <Paper elevation={0} sx={{ border: `1px solid ${BORDER}`, borderRadius: 2, overflow: 'hidden' }}>
      <TableContainer><Table size="small" sx={{ minWidth: 680 }}>
        <TableHead><TableRow sx={{ bgcolor: '#1976D2', '& th': { color: 'white', fontWeight: 900, whiteSpace: 'nowrap' } }}>
          <TableCell>#</TableCell>{columns.map((c, i) => <TableCell key={`${c.key}-${i}`}>{c.label}</TableCell>)}
        </TableRow></TableHead>
        <TableBody>
          {visible.slice(safePage * perPage, (safePage + 1) * perPage).map((row, index) => <TableRow key={row.KeyID ?? row.GrantID ?? row.ApplicationID ?? `${safePage}-${index}`} sx={{ bgcolor: index % 2 ? '#fff' : 'rgba(25,118,210,.06)', '&:hover': { bgcolor: 'rgba(25,118,210,.10)' } }}>
            <TableCell><Box sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 36, height: 36, borderRadius: '50%', border: `2px solid ${BORDER}`, bgcolor: 'rgba(25,118,210,.06)', color: '#1976D2', fontWeight: 900 }}>{safePage * perPage + index + 1}</Box></TableCell>
            {columns.map((c, i) => <TableCell key={`${c.key}-${i}`} sx={{ py: 1.2 }}>{c.render ? c.render(row) : String(row?.[c.key] ?? '—')}</TableCell>)}
          </TableRow>)}
          {!visible.length && <TableRow><TableCell colSpan={columns.length + 1} align="center" sx={{ py: 3 }}>{empty}</TableCell></TableRow>}
        </TableBody>
      </Table></TableContainer>
      <TablePagination component="div" count={visible.length} page={safePage} onPageChange={(_, p) => setPage(p)} rowsPerPage={perPage} onRowsPerPageChange={e => setPerPage(Number(e.target.value))} rowsPerPageOptions={[10, 25, 50, 100]} />
    </Paper>
  </Stack>;
}
